import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText,
  Download,
  Briefcase,
  Plane,
  Award,
  MapPin,
  FileCheck,
  UserCheck,
  GraduationCap,
  Building2,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  Stamp,
  Eye,
  Upload,
  UserPlus,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const letterConfig = {
  employment_verification: {
    icon: Briefcase,
    title: 'Employment Verification Letter',
    description: 'For bank loans, credit cards, rental agreements',
    color: 'bg-blue-500/10 text-blue-500 dark:bg-blue-500/20',
    fields: []
  },
  salary_certificate: {
    icon: FileText,
    title: 'Salary Certificate',
    description: 'For loan applications, tax purposes',
    color: 'bg-green-500/10 text-green-500 dark:bg-green-500/20',
    fields: []
  },
  visa_letter: {
    icon: Plane,
    title: 'VISA Support Letter',
    description: 'For visa/immigration applications',
    color: 'bg-purple-500/10 text-purple-500 dark:bg-purple-500/20',
    fields: [
      { key: 'passport_number', label: 'Passport Number', type: 'text', placeholder: 'A12345678' },
      { key: 'destination', label: 'Destination Country', type: 'text', placeholder: 'USA, UK, etc.' },
      { key: 'travel_dates', label: 'Travel Dates', type: 'text', placeholder: '15 Jan 2026 - 30 Jan 2026' },
    ]
  },
  experience_certificate: {
    icon: Award,
    title: 'Experience Certificate',
    description: 'For job applications, proof of work experience',
    color: 'bg-yellow-500/10 text-yellow-500 dark:bg-yellow-500/20',
    fields: [
      { key: 'end_date', label: 'Last Working Date', type: 'date', placeholder: '' },
    ]
  },
  address_proof: {
    icon: MapPin,
    title: 'Address Proof Letter',
    description: 'For KYC, address verification',
    color: 'bg-red-500/10 text-red-500 dark:bg-red-500/20',
    fields: []
  },
  noc: {
    icon: FileCheck,
    title: 'No Objection Certificate',
    description: 'For higher studies, part-time work, moonlighting',
    color: 'bg-indigo-500/10 text-indigo-500 dark:bg-indigo-500/20',
    fields: [
      { key: 'purpose', label: 'Purpose', type: 'text', placeholder: 'Higher studies / Part-time work' },
    ]
  },
  relieving_letter: {
    icon: UserCheck,
    title: 'Relieving Letter',
    description: 'For employees leaving the organization',
    color: 'bg-orange-500/10 text-orange-500 dark:bg-orange-500/20',
    fields: [
      { key: 'end_date', label: 'Last Working Date', type: 'date', placeholder: '' },
    ]
  },
  bonafide: {
    icon: Building2,
    title: 'Bonafide Certificate',
    description: 'Proof of current employment',
    color: 'bg-teal-500/10 text-teal-500 dark:bg-teal-500/20',
    fields: []
  },
  internship: {
    icon: GraduationCap,
    title: 'Internship Certificate',
    description: 'For interns completing their program',
    color: 'bg-pink-500/10 text-pink-500 dark:bg-pink-500/20',
    fields: [
      { key: 'start_date', label: 'Start Date', type: 'date', placeholder: '' },
      { key: 'end_date', label: 'End Date', type: 'date', placeholder: '' },
      { key: 'project', label: 'Project Name', type: 'text', placeholder: 'AI/ML Development' },
    ]
  },
};

const statusConfig = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  approved: { label: 'Approved', icon: CheckCircle2, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

const LettersPage = () => {
  const { user, isHR } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [isRequestDialogOpen, setIsRequestDialogOpen] = useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [reviewRequest, setReviewRequest] = useState(null);
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [activeTab, setActiveTab] = useState('request');

  // HR-uploaded letters (Offer, Appointment, Salary Revision) — kind-driven
  const [myAppointmentLetter, setMyAppointmentLetter] = useState(null);
  const [mySalaryRevisionLetter, setMySalaryRevisionLetter] = useState(null);
  const [myOfferLetter, setMyOfferLetter] = useState(null);
  const [hrLetterKind, setHrLetterKind] = useState('appointment'); // 'offer' | 'appointment' | 'salary_revision'
  const [apptEmployees, setApptEmployees] = useState([]);
  const [apptDialogOpen, setApptDialogOpen] = useState(false);
  const [apptTargetEmp, setApptTargetEmp] = useState('');
  const [apptFile, setApptFile] = useState(null);
  const [apptUploading, setApptUploading] = useState(false);
  const [apptCoverage, setApptCoverage] = useState({});     // employee_id -> appointment meta
  const [salaryCoverage, setSalaryCoverage] = useState({}); // employee_id -> salary_revision meta
  const [offerCoverage, setOfferCoverage] = useState({});   // employee_id -> offer meta
  const [apptDownloading, setApptDownloading] = useState(false);

  const LETTER_KIND_LABELS = { offer: 'Offer Letter', appointment: 'Appointment Letter', salary_revision: 'Salary Revision Letter' };
  const coverageByKind = { offer: offerCoverage, appointment: apptCoverage, salary_revision: salaryCoverage };
  const setCoverageByKind = { offer: setOfferCoverage, appointment: setApptCoverage, salary_revision: setSalaryCoverage };
  const activeCoverage = coverageByKind[hrLetterKind] || {};
  const setActiveCoverage = setCoverageByKind[hrLetterKind];

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/letter-requests`, { withCredentials: true });
      setRequests(res.data);
    } catch (err) {
      console.error('Failed to fetch letter requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Fetch own Offer + Appointment + Salary Revision Letter metadata (all users)
  useEffect(() => {
    axios.get(`${API_URL}/api/hr-letters/me/offer`, { withCredentials: true })
      .then(res => setMyOfferLetter(res.data?.available ? res.data : null))
      .catch(() => setMyOfferLetter(null));
    axios.get(`${API_URL}/api/hr-letters/me/appointment`, { withCredentials: true })
      .then(res => setMyAppointmentLetter(res.data?.available ? res.data : null))
      .catch(() => setMyAppointmentLetter(null));
    axios.get(`${API_URL}/api/hr-letters/me/salary_revision`, { withCredentials: true })
      .then(res => setMySalaryRevisionLetter(res.data?.available ? res.data : null))
      .catch(() => setMySalaryRevisionLetter(null));
  }, []);

  // HR/Admin — fetch employee list + coverage maps for ALL letter kinds
  useEffect(() => {
    if (!isHR) return;
    axios.get(`${API_URL}/api/employees`, { withCredentials: true })
      .then(async res => {
        const emps = res.data || [];
        setApptEmployees(emps);
        // Fetch coverage for all kinds in parallel (silently ignore 404s = no letter yet)
        const [offerResults, apptResults, salaryResults] = await Promise.all([
          Promise.all(emps.map(e =>
            axios.get(`${API_URL}/api/hr-letters/offer/${e.id}`, { withCredentials: true })
              .then(r => [e.id, r.data]).catch(() => [e.id, null])
          )),
          Promise.all(emps.map(e =>
            axios.get(`${API_URL}/api/hr-letters/appointment/${e.id}`, { withCredentials: true })
              .then(r => [e.id, r.data]).catch(() => [e.id, null])
          )),
          Promise.all(emps.map(e =>
            axios.get(`${API_URL}/api/hr-letters/salary_revision/${e.id}`, { withCredentials: true })
              .then(r => [e.id, r.data]).catch(() => [e.id, null])
          )),
        ]);
        setOfferCoverage(Object.fromEntries(offerResults));
        setApptCoverage(Object.fromEntries(apptResults));
        setSalaryCoverage(Object.fromEntries(salaryResults));
      })
      .catch(() => { /* silent */ });
  }, [isHR]);

  // ---- Offer Letter → CEO (Admin) approval workflow ----
  const [offerApprovals, setOfferApprovals] = useState([]);
  const [offerUpEmp, setOfferUpEmp] = useState('');
  const [offerUpEmail, setOfferUpEmail] = useState('');
  const [offerUpFile, setOfferUpFile] = useState(null);
  const [offerBusy, setOfferBusy] = useState(false);

  const fetchOfferApprovals = useCallback(() => {
    if (!isHR) return;
    axios.get(`${API_URL}/api/offer-approvals`, { withCredentials: true })
      .then(r => setOfferApprovals(r.data || []))
      .catch(() => setOfferApprovals([]));
  }, [isHR]);

  useEffect(() => { fetchOfferApprovals(); }, [fetchOfferApprovals]);

  const submitOfferApproval = async () => {
    if (!offerUpEmp || !offerUpFile) { toast.error('Select an employee and a PDF'); return; }
    setOfferBusy(true);
    try {
      const form = new FormData();
      form.append('file', offerUpFile);
      form.append('personal_email', offerUpEmail || '');
      await axios.post(`${API_URL}/api/offer-approvals/${offerUpEmp}`, form, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Offer letter submitted for CEO approval');
      setOfferUpEmp(''); setOfferUpEmail(''); setOfferUpFile(null);
      fetchOfferApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit offer');
    } finally { setOfferBusy(false); }
  };

  const decideOffer = async (id, action, remarks = '') => {
    try {
      const res = await axios.post(`${API_URL}/api/offer-approvals/${id}/${action}`, action === 'reject' ? { remarks } : {}, { withCredentials: true });
      toast.success(res.data?.message || (action === 'approve' ? 'Offer approved & emailed' : 'Offer rejected'));
      fetchOfferApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const submitReject = async () => {
    if (!rejectTarget) return;
    await decideOffer(rejectTarget.id, 'reject', rejectNote.trim());
    setRejectTarget(null);
    setRejectNote('');
  };

  const openApptUpload = (empId, kind = 'appointment') => {
    setHrLetterKind(kind);
    setApptTargetEmp(empId);
    setApptFile(null);
    setApptDialogOpen(true);
  };

  const submitApptUpload = async () => {
    if (!apptTargetEmp || !apptFile) { toast.error('Pick an employee and PDF file'); return; }
    setApptUploading(true);
    try {
      const form = new FormData();
      form.append('file', apptFile);
      await axios.post(`${API_URL}/api/hr-letters/${hrLetterKind}/${apptTargetEmp}`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const meta = await axios.get(`${API_URL}/api/hr-letters/${hrLetterKind}/${apptTargetEmp}`, { withCredentials: true });
      setCoverageByKind[hrLetterKind](prev => ({ ...prev, [apptTargetEmp]: meta.data }));
      toast.success(`${LETTER_KIND_LABELS[hrLetterKind]} uploaded`);
      setApptDialogOpen(false);
      setApptFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setApptUploading(false); }
  };

  const downloadHrLetter = async (empId, kind = 'appointment') => {
    setApptDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/hr-letters/${kind}/${empId}/download`, {
        withCredentials: true, responseType: 'blob',
      });
      const cov = coverageByKind[kind] || {};
      const myMeta = { offer: myOfferLetter, appointment: myAppointmentLetter, salary_revision: mySalaryRevisionLetter }[kind];
      const meta = cov[empId] || myMeta || {};
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = meta.filename || `${kind}_letter.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    } finally { setApptDownloading(false); }
  };

  const deleteHrLetter = async (empId, empName, kind = 'appointment') => {
    if (!window.confirm(`Remove ${LETTER_KIND_LABELS[kind]} for ${empName}? A superseded copy is retained for audit.`)) return;
    try {
      await axios.delete(`${API_URL}/api/hr-letters/${kind}/${empId}`, { withCredentials: true });
      setCoverageByKind[kind](prev => ({ ...prev, [empId]: null }));
      toast.success(`${LETTER_KIND_LABELS[kind]} removed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Backward-compat aliases used further down (Appointment card in Request tab, etc.)
  const downloadAppointmentLetter = (empId) => downloadHrLetter(empId, 'appointment');
  const deleteAppointmentLetter = (empId, name) => deleteHrLetter(empId, name, 'appointment');

  // Employee downloads THEIR OWN letter — keyed on the caller's employee record, not users._id.
  const downloadMyHrLetter = async (kind) => {
    setApptDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/hr-letters/me/${kind}/download`, {
        withCredentials: true, responseType: 'blob',
      });
      const myMeta = { offer: myOfferLetter, appointment: myAppointmentLetter, salary_revision: mySalaryRevisionLetter }[kind] || {};
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = myMeta.filename || `${kind}_letter.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    } finally { setApptDownloading(false); }
  };

  const handleSubmitRequest = async () => {
    if (!selectedLetter) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/letter-requests`, {
        letter_type: selectedLetter,
        reason,
        additional_data: Object.keys(formData).length > 0 ? formData : null,
      }, { withCredentials: true });
      toast.success('Letter request submitted! HR will review it shortly.');
      setIsRequestDialogOpen(false);
      setFormData({});
      setReason('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!reviewRequest) return;
    setIsSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/letter-requests/${reviewRequest.id}/approve`, {
        remarks: reviewRemarks,
      }, { withCredentials: true });
      toast.success('Letter approved and generated!');
      setIsReviewDialogOpen(false);
      setReviewRemarks('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!reviewRequest) return;
    setIsSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/letter-requests/${reviewRequest.id}/reject`, {
        remarks: reviewRemarks,
      }, { withCredentials: true });
      toast.success('Letter request rejected.');
      setIsReviewDialogOpen(false);
      setReviewRemarks('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async (requestId) => {
    try {
      const response = await axios.get(`${API_URL}/api/letter-requests/${requestId}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `letter_${requestId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Letter downloaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download letter');
    }
  };

  const openRequestDialog = (letterType) => {
    setSelectedLetter(letterType);
    setFormData({});
    setReason('');
    setIsRequestDialogOpen(true);
  };

  const openReviewDialog = (req) => {
    setReviewRequest(req);
    setReviewRemarks('');
    setIsReviewDialogOpen(true);
  };

  const config = selectedLetter ? letterConfig[selectedLetter] : null;
  const pendingRequests = requests.filter(r => r.status === 'pending');
  const myRequests = requests.filter(r => r.employee_id === user?.id);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="letters-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Stamp className="w-7 h-7 text-primary" />
          Employee Letters
        </h1>
        <p className="text-muted-foreground">Request and manage official letters and certificates</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="letters-tabs">
          <TabsTrigger value="request" data-testid="tab-request">
            <Send className="w-4 h-4 mr-1.5" />
            Request Letter
          </TabsTrigger>
          <TabsTrigger value="my-requests" data-testid="tab-my-requests">
            <FileText className="w-4 h-4 mr-1.5" />
            My Requests
            {myRequests.filter(r => r.status === 'pending').length > 0 && (
              <span className="ml-1.5 w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">
                {myRequests.filter(r => r.status === 'pending').length}
              </span>
            )}
          </TabsTrigger>
          {isHR && (
            <TabsTrigger value="appointment" data-testid="tab-appointment">
              <UserPlus className="w-4 h-4 mr-1.5" />
              HR Letters
            </TabsTrigger>
          )}
          {isHR && (
            <TabsTrigger value="manage" data-testid="tab-manage">
              <Eye className="w-4 h-4 mr-1.5" />
              Manage Requests
              {pendingRequests.length > 0 && (
                <span className="ml-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  {pendingRequests.length}
                </span>
              )}
            </TabsTrigger>
          )}
          {isHR && (
            <TabsTrigger value="offer-approvals" data-testid="tab-offer-approvals">
              <UserPlus className="w-4 h-4 mr-1.5" />
              Offer Approvals
              {offerApprovals.filter(o => o.status === 'pending').length > 0 && (
                <span className="ml-1.5 w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">
                  {offerApprovals.filter(o => o.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab 1: Request Letter */}
        <TabsContent value="request" className="mt-6 space-y-4">
          {/* Offer Letter (HR-uploaded — download only, visible to everyone if uploaded) */}
          {myOfferLetter && (
            <Card className="border-l-4 border-l-amber-500 bg-amber-500/5" data-testid="my-offer-letter-card">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2.5 rounded-lg bg-amber-500/15 text-amber-600">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">Your Offer Letter</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {myOfferLetter.filename}
                      {myOfferLetter.uploaded_at && (
                        <> · uploaded {new Date(myOfferLetter.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={apptDownloading}
                  onClick={() => downloadMyHrLetter('offer')}
                  className="gap-1.5 shrink-0"
                  data-testid="download-my-offer-btn"
                >
                  {apptDownloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  Download
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Appointment Letter (HR-uploaded — download only, visible to everyone if uploaded) */}
          {myAppointmentLetter && (
            <Card className="border-l-4 border-l-primary bg-primary/5" data-testid="my-appointment-letter-card">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2.5 rounded-lg bg-primary/15 text-primary">
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">Your Appointment Letter</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {myAppointmentLetter.filename}
                      {myAppointmentLetter.uploaded_at && (
                        <> · uploaded {new Date(myAppointmentLetter.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={apptDownloading}
                  onClick={() => downloadMyHrLetter('appointment')}
                  className="gap-1.5 shrink-0"
                  data-testid="download-my-appointment-btn"
                >
                  {apptDownloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  Download
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Salary Revision Letter (HR-uploaded — download only) */}
          {mySalaryRevisionLetter && (
            <Card className="border-l-4 border-l-emerald-500 bg-emerald-500/5" data-testid="my-salary-revision-letter-card">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2.5 rounded-lg bg-emerald-500/15 text-emerald-600">
                    <FileCheck className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">Your Salary Revision Letter</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {mySalaryRevisionLetter.filename}
                      {mySalaryRevisionLetter.uploaded_at && (
                        <> · uploaded {new Date(mySalaryRevisionLetter.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={apptDownloading}
                  onClick={() => downloadMyHrLetter('salary_revision')}
                  className="gap-1.5 shrink-0"
                  data-testid="download-my-salary-revision-btn"
                >
                  {apptDownloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  Download
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(letterConfig).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <Card
                  key={key}
                  className="cursor-pointer hover:border-primary/50 transition-all hover:shadow-md group"
                  onClick={() => openRequestDialog(key)}
                  data-testid={`letter-${key}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${cfg.color} transition-transform group-hover:scale-110`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm">{cfg.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">{cfg.description}</p>
                        <p className="text-xs text-primary mt-2 font-medium flex items-center gap-1">
                          <Send className="w-3 h-3" />
                          Request
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* HR Tab: HR-uploaded Letters management (Appointment + Salary Revision) */}
        {isHR && (
          <TabsContent value="appointment" className="mt-6" data-testid="appointment-tab-content">
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" /> HR-Uploaded Letters</CardTitle>
                  <p className="text-xs text-muted-foreground">Upload PDF letters per employee. Only that employee (or HR/Admin) can download it.</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 rounded-md bg-muted/40 p-0.5" data-testid="letter-kind-toggle">
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs font-medium rounded ${hrLetterKind === 'offer' ? 'bg-background shadow' : 'text-muted-foreground'}`}
                    onClick={() => setHrLetterKind('offer')}
                    data-testid="kind-btn-offer"
                  >
                    Offer
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs font-medium rounded ${hrLetterKind === 'appointment' ? 'bg-background shadow' : 'text-muted-foreground'}`}
                    onClick={() => setHrLetterKind('appointment')}
                    data-testid="kind-btn-appointment"
                  >
                    Appointment
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs font-medium rounded ${hrLetterKind === 'salary_revision' ? 'bg-background shadow' : 'text-muted-foreground'}`}
                    onClick={() => setHrLetterKind('salary_revision')}
                    data-testid="kind-btn-salary-revision"
                  >
                    Salary Revision
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {apptEmployees.filter(e => e.status === 'active').map(emp => {
                    const meta = activeCoverage[emp.id];
                    return (
                      <div key={emp.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-background hover:bg-muted/30 transition" data-testid={`appt-row-${emp.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{emp.name} <span className="text-muted-foreground text-xs">· {emp.employee_id || 'no code'}</span></p>
                          <p className="text-xs text-muted-foreground truncate">
                            {meta
                              ? <>Uploaded {new Date(meta.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} · {meta.filename}</>
                              : <span className="text-amber-700 dark:text-amber-400">No {LETTER_KIND_LABELS[hrLetterKind].toLowerCase()} uploaded yet</span>
                            }
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {meta && (
                            <Button size="sm" variant="outline" onClick={() => downloadHrLetter(emp.id, hrLetterKind)} className="gap-1 text-xs h-7" data-testid={`hr-download-appt-${emp.id}`}>
                              <Download className="w-3 h-3" /> View
                            </Button>
                          )}
                          <Button size="sm" onClick={() => openApptUpload(emp.id, hrLetterKind)} className="gap-1 text-xs h-7" data-testid={`hr-upload-appt-${emp.id}`}>
                            <Upload className="w-3 h-3" /> {meta ? 'Replace' : 'Upload'}
                          </Button>
                          {meta && (
                            <Button size="sm" variant="ghost" onClick={() => deleteHrLetter(emp.id, emp.name, hrLetterKind)} className="h-7 w-7 p-0 text-rose-600" data-testid={`hr-delete-appt-${emp.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {apptEmployees.filter(e => e.status === 'active').length === 0 && (
                    <p className="text-center text-muted-foreground text-sm py-8">No active employees.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Tab 2: My Requests */}
        <TabsContent value="my-requests" className="mt-6">
          {loadingRequests ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : myRequests.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <FileText className="w-12 h-12 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No letter requests yet</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Go to "Request Letter" to submit a new request</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myRequests.map((req) => {
                const cfg = letterConfig[req.letter_type];
                const Icon = cfg?.icon || FileText;
                return (
                  <Card key={req.id} data-testid={`my-request-${req.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`p-2.5 rounded-lg ${cfg?.color || 'bg-muted'}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm">{req.letter_type_label}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              Requested {new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              {req.reason && ` — ${req.reason}`}
                            </p>
                            {req.remarks && req.status !== 'pending' && (
                              <p className="text-xs text-muted-foreground mt-1">
                                <span className="font-medium">HR Remarks:</span> {req.remarks}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <StatusBadge status={req.status} />
                          {req.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(req.id)}
                              className="gap-1.5"
                              data-testid={`download-btn-${req.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                              Download
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Tab 3: Manage Requests (HR/Admin only) */}
        {isHR && (
          <TabsContent value="manage" className="mt-6">
            {loadingRequests ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : requests.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <CheckCircle2 className="w-12 h-12 text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">No letter requests</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {requests.map((req) => {
                  const cfg = letterConfig[req.letter_type];
                  const Icon = cfg?.icon || FileText;
                  return (
                    <Card key={req.id} className={req.status === 'pending' ? 'border-amber-300/50' : ''} data-testid={`manage-request-${req.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`p-2.5 rounded-lg ${cfg?.color || 'bg-muted'}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm">{req.letter_type_label}</p>
                              <p className="text-xs text-muted-foreground">
                                <span className="font-medium">{req.employee_name}</span> — {new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                              {req.reason && (
                                <p className="text-xs text-muted-foreground mt-0.5">Reason: {req.reason}</p>
                              )}
                              {req.reviewed_by && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Reviewed by {req.reviewed_by} on {new Date(req.reviewed_at).toLocaleDateString('en-IN')}
                                  {req.remarks && ` — "${req.remarks}"`}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <StatusBadge status={req.status} />
                            {req.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => openReviewDialog(req)}
                                data-testid={`review-btn-${req.id}`}
                              >
                                Review
                              </Button>
                            )}
                            {req.status === 'approved' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownload(req.id)}
                                className="gap-1.5"
                                data-testid={`hr-download-btn-${req.id}`}
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        )}

        {/* Tab: Offer Approvals (HR uploads → CEO/Admin approves → auto-email to candidate) */}
        {isHR && (
          <TabsContent value="offer-approvals" className="mt-6 space-y-4" data-testid="offer-approvals-tab">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><UserPlus className="w-4 h-4" /> Submit Offer Letter for CEO Approval</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Employee / Candidate</Label>
                    <select
                      value={offerUpEmp}
                      onChange={(e) => {
                        const id = e.target.value;
                        setOfferUpEmp(id);
                        const emp = apptEmployees.find(x => x.id === id);
                        setOfferUpEmail(emp?.personal_email || '');
                      }}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="offer-emp-select"
                    >
                      <option value="">Select employee…</option>
                      {apptEmployees.map(e => <option key={e.id} value={e.id}>{`${e.name} — ${e.employee_id}`}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Candidate Personal Email</Label>
                    <Input type="email" value={offerUpEmail} onChange={(e) => setOfferUpEmail(e.target.value)} placeholder="candidate@gmail.com" data-testid="offer-personal-email" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Offer Letter PDF</Label>
                  <Input type="file" accept="application/pdf" onChange={(e) => setOfferUpFile(e.target.files?.[0] || null)} data-testid="offer-file-input" />
                </div>
                <Button onClick={submitOfferApproval} disabled={offerBusy || !offerUpEmp || !offerUpFile} className="gap-2" data-testid="offer-submit-btn">
                  {offerBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit for Approval
                </Button>
                <p className="text-[11px] text-muted-foreground">The offer goes to the CEO (Admin) for approval. Once approved, it's emailed to the candidate's personal email and becomes their downloadable Offer Letter.</p>
              </CardContent>
            </Card>

            <div className="space-y-3">
              {offerApprovals.length === 0 ? (
                <Card><CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="w-10 h-10 text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground text-sm">No offer letters submitted yet</p>
                </CardContent></Card>
              ) : offerApprovals.map((o) => {
                const sc = { pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', cancelled: 'bg-slate-100 text-slate-600' }[o.status] || 'bg-muted';
                return (
                  <Card key={o.id} className={o.status === 'pending' ? 'border-amber-300/50' : ''} data-testid={`offer-approval-${o.id}`}>
                    <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{o.employee_name}</p>
                          <Badge className={`text-[10px] ${sc}`}>{o.status}</Badge>
                          {o.status === 'approved' && o.email_sent && <Badge variant="outline" className="text-[10px]">Emailed</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{o.designation} · {o.personal_email || 'no personal email'} · {o.filename}</p>
                        <p className="text-[10px] text-muted-foreground">Submitted {o.uploaded_at ? new Date(o.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''} by {o.uploaded_by_name || ''}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => window.open(`${API_URL}/api/offer-approvals/${o.id}/download`, '_blank')} data-testid={`offer-view-${o.id}`}>
                          <Eye className="w-3.5 h-3.5" /> View
                        </Button>
                        {isAdmin && o.status === 'pending' && (
                          <>
                            <Button size="sm" className="gap-1.5 bg-green-600 hover:bg-green-700" onClick={() => decideOffer(o.id, 'approve')} data-testid={`offer-approve-${o.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> Approve & Email
                            </Button>
                            <Button size="sm" variant="outline" className="gap-1.5 text-destructive" onClick={() => { setRejectTarget(o); setRejectNote(''); }} data-testid={`offer-reject-${o.id}`}>
                              <XCircle className="w-3.5 h-3.5" /> Reject
                            </Button>
                          </>
                        )}
                        {!isAdmin && o.status === 'pending' && (
                          <span className="text-[11px] text-muted-foreground">Awaiting CEO approval</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Offer Reject Note Dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="offer-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Offer Letter</DialogTitle>
            <DialogDescription>
              Add a note for {rejectTarget?.uploaded_by_name || 'HR'} explaining what needs to change. It will be emailed to them.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="e.g. The CTC figure is wrong — please correct the base salary and re-submit."
            rows={4}
            data-testid="offer-reject-note"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitReject} data-testid="offer-reject-confirm">Reject & Email HR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Dialog */}
      <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {config && <config.icon className="w-5 h-5" />}
              Request: {config?.title}
            </DialogTitle>
            <DialogDescription>
              Submit a request for this letter. HR will review and approve it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Reason / Purpose</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Required for home loan application with SBI"
                rows={2}
                data-testid="letter-reason"
              />
            </div>

            {config?.fields?.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label>{field.label}</Label>
                <Input
                  type={field.type}
                  value={formData[field.key] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  data-testid={`letter-field-${field.key}`}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsRequestDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRequest}
              disabled={isSubmitting}
              className="gap-2"
              data-testid="submit-letter-request-btn"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="w-4 h-4" /> Submit Request</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog (HR) */}
      <Dialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Letter Request</DialogTitle>
            <DialogDescription>
              Approve or reject this letter request.
            </DialogDescription>
          </DialogHeader>

          {reviewRequest && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Employee</p>
                  <p className="font-medium">{reviewRequest.employee_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Letter Type</p>
                  <p className="font-medium">{reviewRequest.letter_type_label}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Requested On</p>
                  <p className="font-medium">{new Date(reviewRequest.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Reason</p>
                  <p className="font-medium">{reviewRequest.reason || '—'}</p>
                </div>
              </div>

              {reviewRequest.additional_data && Object.keys(reviewRequest.additional_data).length > 0 && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Additional Details</p>
                  {Object.entries(reviewRequest.additional_data).map(([k, v]) => (
                    <p key={k} className="text-sm">
                      <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}:</span> {v}
                    </p>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <Label>Remarks (optional)</Label>
                <Textarea
                  value={reviewRemarks}
                  onChange={(e) => setReviewRemarks(e.target.value)}
                  placeholder="Add any remarks for the employee..."
                  rows={2}
                  data-testid="review-remarks"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={isSubmitting}
              className="gap-1.5"
              data-testid="reject-letter-btn"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              Reject
            </Button>
            <Button
              onClick={handleApprove}
              disabled={isSubmitting}
              className="gap-1.5"
              data-testid="approve-letter-btn"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Approve & Generate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* HR HR-letter upload dialog (Appointment / Salary Revision) */}
      <Dialog open={apptDialogOpen} onOpenChange={setApptDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="appt-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" /> Upload {LETTER_KIND_LABELS[hrLetterKind]}</DialogTitle>
            <DialogDescription>
              Uploading for <b>{apptEmployees.find(e => e.id === apptTargetEmp)?.name || '—'}</b>. PDF only, max 10 MB. Any previously uploaded copy is retained for audit and marked superseded.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>PDF file</Label>
              <Input type="file" accept="application/pdf" onChange={(e) => setApptFile(e.target.files?.[0] || null)} data-testid="appt-file-input" />
              {apptFile && (
                <p className="text-xs text-muted-foreground">{apptFile.name} · {(apptFile.size / 1024).toFixed(1)} KB</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setApptDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitApptUpload} disabled={apptUploading || !apptFile} className="gap-1.5" data-testid="appt-upload-submit">
              {apptUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LettersPage;
